import React from 'react';
import styled from 'styled-components';

// Import all your local Garnet images
import demantoidGarnet from '../../assets/image/Demantoid Garnet.jpg';
import tsavoriteGarnet from '../../assets/image/Tsavorite Garnet.jpeg';
import rhodoliteGarnet from '../../assets/image/Rhodolite Garnet.jpeg';
import spessartiteGarnet from '../../assets/image/Spessartite Garnet.jpeg';
import maliGarnet from '../../assets/image/Mali Garne.jpeg';
import pyropeGarnet from '../../assets/image/Pyrope Garnet.jpeg';

const PageContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const HeroSection = styled.section`
  height: 60vh;
  min-height: 500px;
  background: linear-gradient(rgba(0,0,0,0.3), rgba(0,0,0,0.3)),
              url('https://images.unsplash.com/photo-1615655114865-4cc1bda5901e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1920&q=80');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: white;
  margin-bottom: 60px;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0,0,0,0.3);
    z-index: 1;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
`;

const HeroTitle = styled.h1`
  font-size: 64px;
  margin-bottom: 20px;
  letter-spacing: 3px;
  font-weight: 300;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
  
  span {
    color: #c4a484;
    font-weight: 400;
  }
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 18px;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.8;
  opacity: 0.95;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.3);
`;

const SectionTitle = styled.h2`
  font-size: 42px;
  text-align: center;
  margin: 60px 0 40px;
  color: #1a2a3a;
  font-weight: 300;
  
  &:after {
    content: '';
    display: block;
    width: 80px;
    height: 2px;
    background: #c4a484;
    margin: 20px auto;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  margin-top: 40px;
`;

const GemCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 15px 30px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0,0,0,0.15);
  }
`;

const GemImageWrapper = styled.div`
  width: 100%;
  height: 280px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 50px;
    background: linear-gradient(to top, rgba(0,0,0,0.3), transparent);
    pointer-events: none;
  }
`;

const GemImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  background-color: #f8f9fa;
  transition: transform 0.5s ease;
  
  ${GemCard}:hover & {
    transform: scale(1.05);
  }
`;

const GemInfo = styled.div`
  padding: 25px;
`;

const GemName = styled.h3`
  font-size: 24px;
  color: #1a2a3a;
  margin-bottom: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
`;

const GemDetails = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 15px;
  font-size: 14px;
`;

const GemProperties = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  margin-top: 15px;
  padding-top: 15px;
  border-top: 1px solid #f0f0f0;
`;

const Property = styled.span`
  background: #f8f9fa;
  padding: 5px 15px;
  border-radius: 20px;
  font-size: 12px;
  color: #555;
  border: 1px solid #e0e0e0;
  display: inline-flex;
  align-items: center;
  
  &:before {
    content: '✦';
    color: #c4a484;
    margin-right: 5px;
    font-size: 10px;
  }
`;

const RarityBadge = styled.span`
  background: ${props => {
    if (props.rarity === 'Rarest') return '#ff4757';
    if (props.rarity === 'Very Rare') return '#9b59b6';
    if (props.rarity === 'Rare') return '#f39c12';
    if (props.rarity === 'Fine') return '#3498db';
    if (props.rarity === 'NEW') return '#2ecc71';
    return '#c4a484';
  }};
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
  white-space: nowrap;
`;

const OriginBadge = styled.span`
  background: #1a2a3a;
  color: white;
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 4px;
  display: inline-block;
  margin-top: 10px;
`;

const ColorIndicator = styled.div`
  width: 100%;
  height: 8px;
  background: ${props => props.color};
  border-radius: 4px;
  margin: 15px 0 10px;
`;

const Garnets = () => {
  // Garnet gems for the grid
  const gems = [
    {
      name: "Demantoid Garnet",
      image: demantoidGarnet,
      description: "The rarest and most valuable garnet, with brilliant green color and horsetail inclusions. Discovered in Russia's Ural Mountains, it has exceptional dispersion (fire) exceeding diamond.",
      properties: ["Hardness: 6.5-7", "Color: Green", "Dispersion: High"],
      origin: "Russia",
      rarity: "Rarest",
      price: "$5,000 - $25,000+ per carat",
      colorBar: "linear-gradient(135deg, #2ecc71, #27ae60)"
    },
    {
      name: "Tsavorite Garnet",
      image: tsavoriteGarnet,
      description: "Vibrant green garnet discovered in 1967 in Kenya, prized for its brilliance and saturation. Named after Tsavo National Park, it offers emerald-like color at a more accessible price.",
      properties: ["Hardness: 7-7.5", "Color: Vivid Green", "Clarity: Excellent"],
      origin: "Kenya/Tanzania",
      rarity: "Very Rare",
      price: "$2,000 - $8,000 per carat",
      colorBar: "linear-gradient(135deg, #27ae60, #229954)"
    },
    {
      name: "Rhodolite Garnet",
      image: rhodoliteGarnet,
      description: "Purple-red garnet with rose-like color, popular in fine jewelry. The name comes from 'rhodon' (rose) and 'lithos' (stone), perfectly describing its beautiful pinkish-red hue.",
      properties: ["Hardness: 7-7.5", "Color: Raspberry", "Pleochroic"],
      origin: "Tanzania/Sri Lanka",
      rarity: "Fine",
      price: "$300 - $1,500 per carat",
      colorBar: "linear-gradient(135deg, #e84393, #c2185b)"
    },
    {
      name: "Spessartite Garnet",
      image: spessartiteGarnet,
      description: "Orange to orange-red garnet with exceptional fire and brilliance. Fine specimens display a vivid mandarin orange color, making them highly sought after.",
      properties: ["Hardness: 7-7.5", "Color: Mandarin Orange", "Fire: High"],
      origin: "Namibia/Nigeria",
      rarity: "Rare",
      price: "$500 - $3,000 per carat",
      colorBar: "linear-gradient(135deg, #f39c12, #e67e22)"
    },
    {
      name: "Mali Garnet",
      image: maliGarnet,
      description: "Greenish-yellow to brownish-yellow garnet with high dispersion. A hybrid between andradite and grossular, discovered in Mali in 1994.",
      properties: ["Hardness: 7-7.5", "Color: Yellow-Green", "Dispersion: High"],
      origin: "Mali",
      rarity: "NEW",
      price: "$400 - $1,200 per carat",
      colorBar: "linear-gradient(135deg, #f1c40f, #d4ac0d)"
    },
    {
      name: "Pyrope Garnet",
      image: pyropeGarnet,
      description: "Blood-red garnet, the classic 'Bohemian garnet' of antique jewelry. The name comes from 'pyropos' meaning fire-like, referring to its intense red color.",
      properties: ["Hardness: 7-7.5", "Color: Blood Red", "Classic"],
      origin: "Czech Republic/Arizona",
      rarity: "Fine",
      price: "$100 - $500 per carat",
      colorBar: "linear-gradient(135deg, #c0392b, #922b21)"
    }
  ];

  return (
    <PageContainer>
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            <span>Garnet</span> Family
          </HeroTitle>
          <HeroSubtitle>
            More than just red • A rainbow of possibilities • Demantoid • Tsavorite • Rhodolite
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>
      
      <SectionTitle>Exceptional Garnets</SectionTitle>
      <Grid>
        {gems.map((gem, index) => (
          <GemCard key={index}>
            <GemImageWrapper>
              <GemImage src={gem.image} alt={gem.name} />
            </GemImageWrapper>
            <GemInfo>
              <GemName>
                {gem.name}
                <RarityBadge rarity={gem.rarity}>{gem.rarity}</RarityBadge>
              </GemName>
              <GemDetails>{gem.description}</GemDetails>
              <OriginBadge>📍 {gem.origin}</OriginBadge>
              <ColorIndicator color={gem.colorBar} />
              <GemProperties>
                {gem.properties.map((prop, i) => (
                  <Property key={i}>{prop}</Property>
                ))}
              </GemProperties>
              <GemDetails style={{ marginTop: '10px', color: '#c4a484', fontWeight: '500' }}>
                {gem.price}
              </GemDetails>
            </GemInfo>
          </GemCard>
        ))}
      </Grid>
    </PageContainer>
  );
};

export default Garnets;